import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ClockIcon,
  CheckIcon,
  XIcon,
  ExclamationIcon,
} from "@heroicons/react/outline";

const PendingVerifications = () => {
  const navigate = useNavigate();
  const [pendingPayments, setPendingPayments] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchPendingPayments();
  }, [filter]);

  const fetchPendingPayments = async () => {
    setLoading(true);
    try {
      // Mock data
      const mockData = [
        {
          _id: "1",
          planNumber: "JP2024001",
          customerName: "John Doe",
          amount: 5000,
          collectedAmount: 5000,
          method: "CASH",
          collectedDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
          enteredBy: "Staff 1",
          daysPending: 2,
          priority: "HIGH",
        },
        {
          _id: "2",
          planNumber: "JP2024002",
          customerName: "Jane Smith",
          amount: 10000,
          collectedAmount: 9950,
          method: "UPI",
          collectedDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
          enteredBy: "Staff 2",
          daysPending: 1,
          priority: "MEDIUM",
          hasVariance: true,
        },
      ];
      setPendingPayments(mockData);
    } catch (error) {
      console.error("Failed to fetch pending payments:", error);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "HIGH":
        return "text-danger bg-danger-light";
      case "MEDIUM":
        return "text-warning bg-warning-light";
      default:
        return "text-secondary bg-secondary-light";
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="bg-background shadow rounded-lg p-6 mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-text">
              Pending Verifications
            </h1>
            <p className="mt-1 text-sm text-neutral-dark">
              {pendingPayments.length} payments awaiting verification
            </p>
          </div>
          <button
            onClick={() => navigate("/jewellery/reports")}
            className="text-neutral-dark hover:text-text"
          >
            ← Back to Reports
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-background shadow rounded-lg p-4 mb-6">
        <div className="flex gap-2">
          <button
            onClick={() => setFilter("all")}
            className={`px-4 py-2 rounded-md ${
              filter === "all"
                ? "bg-primary text-text-on-primary"
                : "bg-neutral-light text-text hover:bg-neutral-dark hover:text-text-on-primary"
            }`}
          >
            All ({pendingPayments.length})
          </button>
          <button
            onClick={() => setFilter("high")}
            className={`px-4 py-2 rounded-md ${
              filter === "high"
                ? "bg-danger text-text-on-primary"
                : "bg-neutral-light text-text hover:bg-danger-light"
            }`}
          >
            High Priority (
            {pendingPayments.filter((p) => p.priority === "HIGH").length})
          </button>
          <button
            onClick={() => setFilter("variance")}
            className={`px-4 py-2 rounded-md ${
              filter === "variance"
                ? "bg-warning text-text-on-primary"
                : "bg-neutral-light text-text hover:bg-warning-light"
            }`}
          >
            With Variance ({pendingPayments.filter((p) => p.hasVariance).length}
            )
          </button>
        </div>
      </div>

      {/* Pending Payments List */}
      <div className="space-y-4">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : pendingPayments.length > 0 ? (
          pendingPayments.map((payment) => (
            <div
              key={payment._id}
              className="bg-background shadow rounded-lg p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <h3 className="text-lg font-medium text-text">
                      {payment.planNumber}
                    </h3>
                    <span
                      className={`ml-3 px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(
                        payment.priority
                      )}`}
                    >
                      {payment.priority} PRIORITY
                    </span>
                    {payment.hasVariance && (
                      <span className="ml-2 px-2 py-1 rounded-full text-xs font-medium bg-warning-light text-warning-dark">
                        <ExclamationIcon className="h-3 w-3 inline mr-1" />
                        VARIANCE
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-neutral-dark">Customer</p>
                      <p className="font-medium">{payment.customerName}</p>
                    </div>
                    <div>
                      <p className="text-neutral-dark">Collected Amount</p>
                      <p className="font-medium">
                        {formatAmount(payment.collectedAmount)}
                      </p>
                      {payment.hasVariance && (
                        <p className="text-xs text-warning">
                          Expected: {formatAmount(payment.amount)}
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="text-neutral-dark">Method</p>
                      <p className="font-medium">{payment.method}</p>
                    </div>
                    <div>
                      <p className="text-neutral-dark">Pending Since</p>
                      <p className="font-medium text-danger">
                        {payment.daysPending} days
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() =>
                      navigate(`/jewellery/payments/verify/${payment._id}`)
                    }
                    className="p-2 bg-success text-text-on-primary rounded-lg hover:bg-success-dark"
                  >
                    <CheckIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => console.log("Reject:", payment._id)}
                    className="p-2 bg-danger text-text-on-primary rounded-lg hover:bg-danger-dark"
                  >
                    <XIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-background shadow rounded-lg p-12 text-center">
            <ClockIcon className="h-12 w-12 text-neutral-dark mx-auto mb-4" />
            <p className="text-neutral-dark">No pending verifications</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PendingVerifications;
